import type { IAction } from "@bbj-types/action";
import type { EnumAppointmentReminderStatus } from "@bbj-types/action/appointmentReminder";
import type { IApply } from "@bbj-types/apply";
import type { ICandidate } from "@bbj-types/candidate";
import type { IContact } from "@bbj-types/contact";
import type { IEnterprise } from "@bbj-types/enterprise";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApply } from "@bbj-types/socialApply";
import type { IUser } from "@bbj-types/user";
import type { ObjectId, Override } from "@bbj-types/utils";
import { useSuspenseWithReload } from "@root/api/tools/axios";
import { useCallback } from "react";
import { axiosAction, axiosActionInstance } from "./axios";

export interface IGetActionsParams {
  skip?: number;
  limit?: number;
  page?: number;
  multiSearch?: string;
  owner?: ObjectId | ObjectId[];
  candidate?: ObjectId | ObjectId[];
  enterprise?: ObjectId | ObjectId[];
  contact?: ObjectId | ObjectId[];
  status?: string;
  type?: string[];
  isAutoGenerate?: boolean;
  display?: boolean;
  active?: boolean;
  startDate?: Date;
  endDate?: Date;
  pinned?: boolean;
  challenges?: ObjectId | ObjectId[];
  authorsActions?: ObjectId[];
  sort?: string;
  dataExclusiveFilters?: Omit<IGetActionsParams, "dataExclusiveFilters"> & { _id?: string[] };
}

export interface IActionCommunication {
  sms?: string;
  email?: string;
}

export interface ITotalsGetActions {
  total: number;
  totalTodo: number;
  totalLate: number;
  totalDone: number;
  totalPinned: number;
  totalMissed: number;
}

export interface IGetActionsData extends Omit<IAction, "candidate" | "apply" | "enterprise" | "owner" | "socialApply"> {
  apply?: Pick<IApply, "_id" | "hiringFollowUp"> & {
    offer: Override<
      Pick<IApply["offer"], "name" | "address" | "contractType" | "enterprise">,
      { enterprise: Pick<IApply["offer"]["enterprise"], "name"> }
    >;
  };
  socialApply?: Override<
    Pick<ISocialApply, "_id" | "socialOffer">,
    { socialOffer: Pick<ISocialOffer, "name" | "offerId" | "address"> }
  >;
  candidate: Pick<ICandidate, "_id" | "firstName" | "lastName" | "isArchived" | "team" | "profileId">;
  enterprise?: Override<IEnterprise, { contacts: IContact[] }>;
  owner: Pick<IUser, "_id" | "name" | "firstName" | "lastName" | "isDisabled">;
  communication?: IActionCommunication;
  appointmentReminderStatus?: EnumAppointmentReminderStatus;
}

export interface IGetActions extends ITotalsGetActions {
  data: IGetActionsData[];
  limit: number;
  skip: number;
  count: number;
}

export const useGetActions = () => {
  const [state, execute, cancel] = axiosAction<IGetActions, unknown, unknown>(
    {
      url: "/",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (filter: IGetActionsParams) => {
      return execute({
        params: filter,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export type TGetAction = IGetActionsData;

export const useGetActionById = () => {
  const [state, execute, cancel] = axiosAction<TGetAction, void, unknown>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: ObjectId) =>
      execute({
        url: `/${id}`,
      }),
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetActionsSuspense = (params: IGetActionsParams) => {
  return useSuspenseWithReload(
    () =>
      axiosActionInstance.get<IGetActions>("/", {
        withCredentials: true,
        params,
      }),
    [params]
  );
};

export const useGetCandidateActions = () => {
  const [state, execute, cancel] = axiosAction<IGetActions, unknown, unknown>(
    {
      url: "/candidate",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: ObjectId, filter: Omit<IGetActionsParams, "candidate">) => {
      return execute({
        url: `/candidate/${candidateId}`,
        params: filter,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
